import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Layers, X } from "lucide-react";

function AttributeEditor({ open, onOpenChange, initial, onSaved }) {
    const isEdit = Boolean(initial?.id);
    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [values, setValues] = useState([]); // [{id?, value}]
    const [newValue, setNewValue] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(initial?.name || "");
            setSlug(initial?.slug || "");
            setValues(initial?.values ? initial.values.map((v) => ({ ...v })) : []);
            setNewValue("");
        }
    }, [open, initial]);

    const addValue = () => {
        const v = newValue.trim();
        if (!v) return;
        setValues((arr) => [...arr, { value: v }]);
        setNewValue("");
    };

    const removeValue = (idx) => setValues((arr) => arr.filter((_, i) => i !== idx));

    const updateValue = (idx, v) => setValues((arr) => arr.map((x, i) => (i === idx ? { ...x, value: v } : x)));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (values.length === 0) {
            toast.error("Add at least one value (e.g. Red, Blue, Small)");
            return;
        }
        setSaving(true);
        try {
            const payload = { name, slug: slug?.trim() || undefined, values };
            if (isEdit) {
                await api.patch(`/admin/attributes/${initial.id}`, payload);
                toast.success("Attribute updated");
            } else {
                await api.post("/admin/attributes", payload);
                toast.success("Attribute created");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="attribute-editor-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                        {isEdit ? "Edit attribute" : "New attribute"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Name</Label>
                            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Color, Size…" className="mt-1.5 rounded-xl" data-testid="attribute-form-name" />
                        </div>
                        <div>
                            <Label>Slug (auto)</Label>
                            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto" className="mt-1.5 rounded-xl" />
                        </div>
                    </div>
                    <div>
                        <Label>Values</Label>
                        <p className="text-xs text-ink-700 mb-2">These become selectable variant options on the product.</p>
                        <div className="flex gap-2 mb-3">
                            <Input
                                value={newValue}
                                onChange={(e) => setNewValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addValue(); } }}
                                placeholder="Add value (Red, Small, 6mm…)"
                                className="rounded-xl"
                                data-testid="attribute-value-input"
                            />
                            <Button type="button" onClick={addValue} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white" data-testid="attribute-value-add-btn">
                                Add
                            </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {values.map((v, i) => (
                                <div key={i} className="inline-flex items-center gap-1 bg-brand-50 text-brand-700 rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`attribute-value-chip-${i}`}>
                                    <input
                                        value={v.value}
                                        onChange={(e) => updateValue(i, e.target.value)}
                                        className="bg-transparent border-0 outline-none text-sm w-auto min-w-[3ch]"
                                        size={Math.max(3, v.value.length)}
                                    />
                                    <button type="button" onClick={() => removeValue(i)} className="h-5 w-5 rounded-full bg-white text-accent-700 flex items-center justify-center" aria-label="Remove">
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                            {values.length === 0 && <span className="text-xs text-ink-400">No values yet.</span>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="attribute-form-save">
                            {saving ? "Saving…" : isEdit ? "Save changes" : "Create attribute"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function AttributesTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const reload = () => api.get("/admin/attributes").then(({ data }) => setItems(data));
    useEffect(() => { reload(); }, []);

    const remove = async (id) => {
        if (!confirm("Delete this attribute? Products using it will keep their existing variants but you won't be able to add new variants for this attribute.")) return;
        try {
            await api.delete(`/admin/attributes/${id}`);
            toast.success("Attribute deleted");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="space-y-4" data-testid="attributes-tab">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="font-heading text-2xl">Attributes</h3>
                    <p className="text-sm text-ink-700">Define attributes (Color, Size…) once, reuse across products to generate variants.</p>
                </div>
                <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="attribute-new-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> New attribute
                </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                {items.map((a) => (
                    <div key={a.id} className="rounded-2xl bg-white border border-gray-100 p-4" data-testid={`attribute-row-${a.id}`}>
                        <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-lg bg-accent-50 text-accent-600 flex items-center justify-center">
                                    <Layers className="h-4 w-4" />
                                </div>
                                <div>
                                    <div className="font-semibold">{a.name}</div>
                                    <div className="text-xs text-ink-700">{a.values?.length || 0} values</div>
                                </div>
                            </div>
                            <div className="flex gap-1.5">
                                <Button size="icon" variant="outline" className="rounded-full h-8 w-8" onClick={() => { setEditing(a); setOpen(true); }} data-testid={`attribute-edit-${a.id}`}>
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="outline" className="rounded-full h-8 w-8 text-accent-700 hover:bg-accent-50" onClick={() => remove(a.id)} data-testid={`attribute-delete-${a.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {(a.values || []).map((v) => (
                                <span key={v.id} className="text-[11px] rounded-full bg-brand-50 text-brand-700 px-2.5 py-1">{v.value}</span>
                            ))}
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="col-span-full text-center py-10 text-ink-700">No attributes yet.</div>
                )}
            </div>
            <AttributeEditor open={open} onOpenChange={setOpen} initial={editing} onSaved={reload} />
        </div>
    );
}
